import type { DicomJson, DicomWebClient } from '../dicomweb';
import type { CsEnabledElement, CsImage } from '../globals';
import { firstPart } from './retrieve';
import {
  type CsLut,
  type InputRange,
  type VoiFunction,
  type VoiWindow,
  functionLut,
  headerVoiFunction,
  headerWindows,
  isUsableLut,
  lutWindow,
  lutWords,
  parseLutSequence,
  parseVoiFunction,
  sameWindow,
  stretchLut,
} from './voilut';

/**
 * Cornerstone side of display/voilut.ts:
 * - a metadata provider that gives the image loader the VOI / Modality LUT Sequences and the VOI LUT
 *   Function in both load modes (the WADO-RS provider of wado-image-loader 4.13.2 reads only
 *   WindowCenter/Width, so in fast mode a mammogram that carries only a VOI LUT was shown full-range);
 * - `syncViewportLut`, run before every draw, which keeps `viewport.voiLUT` / `viewport.modalityLUT`
 *   belonging to the image on screen and matching the current window.
 */

/** Above the loaders' own providers (registered at the default priority 0). */
const PROVIDER_PRIORITY = 100;
const VOI_FUNCTION_TAG = 'x00281056';
const BULK_ACCEPT = 'multipart/related; type="application/octet-stream"';

interface VoiModule {
  windowCenter?: number[];
  windowWidth?: number[];
  voiLUTSequence?: CsLut[];
  voiLUTFunction?: VoiFunction;
}

interface ModalityModule {
  rescaleIntercept?: number;
  rescaleSlope?: number;
  modalityLUTSequence?: CsLut[];
}

type Provider = (type: string, imageId: string) => unknown;

interface LoaderInternals {
  wadors: { metaData: { metaDataProvider: Provider }; metaDataManager: { get(imageId: string): DicomJson | undefined } };
  wadouri: {
    metaData: { metaDataProvider: Provider };
    parseImageId(imageId: string): { url: string };
    dataSetCacheManager: { get?(url: string): { string(tag: string): string | undefined } | undefined };
  };
}

function loader(): LoaderInternals {
  return window.cornerstoneWADOImageLoader as unknown as LoaderInternals;
}

function hasWindow(module: VoiModule): boolean {
  const width = module.windowWidth?.[0];
  return Number.isFinite(module.windowCenter?.[0]) && Number.isFinite(width) && (width as number) > 0;
}

/** When the header has no window but has a LUT, the LUT's input range is the image's own window. */
function withLutWindow(module: VoiModule): VoiModule {
  const lut = module.voiLUTSequence?.[0];
  if (!lut || hasWindow(module)) return module;
  const window = lutWindow(lut);
  return { ...module, windowCenter: [window.center], windowWidth: [window.width] };
}

function fromWadors(type: string, imageId: string): unknown {
  const { wadors } = loader();
  const base = wadors.metaData.metaDataProvider(type, imageId) as Record<string, unknown> | undefined;
  const json = wadors.metaDataManager.get(imageId);
  if (!json) return base;
  if (type === 'modalityLutModule') {
    const sequence = parseLutSequence(json, 'modality');
    return { ...(base ?? {}), modalityLUTSequence: sequence.length ? sequence : undefined } satisfies ModalityModule;
  }
  const windows = headerWindows(json);
  const sequence = parseLutSequence(json, 'voi');
  return withLutWindow({
    ...(base ?? {}),
    windowCenter: windows.length ? windows.map((w) => w.center) : undefined,
    windowWidth: windows.length ? windows.map((w) => w.width) : undefined,
    voiLUTSequence: sequence.length ? sequence : undefined,
    voiLUTFunction: headerVoiFunction(json),
  });
}

function wadouriVoiFunction(imageId: string): VoiFunction {
  try {
    const { wadouri } = loader();
    return parseVoiFunction(wadouri.dataSetCacheManager.get?.(wadouri.parseImageId(imageId).url)?.string(VOI_FUNCTION_TAG));
  } catch {
    return 'LINEAR';
  }
}

function fromWadouri(type: string, imageId: string): unknown {
  const base = loader().wadouri.metaData.metaDataProvider(type, imageId) as (VoiModule & ModalityModule) | undefined;
  if (!base) return undefined;
  if (type === 'modalityLutModule') {
    const sequence = (base.modalityLUTSequence ?? []).filter(isUsableLut);
    return { ...base, modalityLUTSequence: sequence.length ? sequence : undefined };
  }
  const sequence = (base.voiLUTSequence ?? []).filter(isUsableLut);
  return withLutWindow({ ...base, voiLUTSequence: sequence.length ? sequence : undefined, voiLUTFunction: wadouriVoiFunction(imageId) });
}

function lutProvider(type: string, imageId: string): unknown {
  if (type !== 'voiLutModule' && type !== 'modalityLutModule') return undefined;
  if (imageId.startsWith('wadors:')) return fromWadors(type, imageId);
  if (imageId.startsWith('wadouri:') || imageId.startsWith('dicomweb:')) return fromWadouri(type, imageId);
  return undefined;
}

let registered = false;
export function registerLutProvider(): void {
  if (registered) return;
  registered = true;
  window.cornerstone.metaData.addProvider(lutProvider, PROVIDER_PRIORITY);
}

// --- Bulk LUT data ---------------------------------------------------------------------------

interface LutDataElement {
  vr: string;
  Value?: unknown[];
  BulkDataURI?: string;
}

/** LUT Data elements still pointing at bulk data, with the entry count their descriptor declares. */
function bulkLutData(json: DicomJson): Array<{ element: LutDataElement; entries: number }> {
  const found: Array<{ element: LutDataElement; entries: number }> = [];
  for (const tag of ['00283010', '00283000']) {
    for (const item of (json[tag]?.Value ?? []) as DicomJson[]) {
      const element = item?.['00283006'] as LutDataElement | undefined;
      const declared = Number(item?.['00283002']?.Value?.[0]);
      if (element?.BulkDataURI && !element.Value) found.push({ element, entries: declared === 0 ? 65536 : declared });
    }
  }
  return found;
}

/**
 * PACS send large LUT Data (0028,3006) as a BulkDataURI rather than inline. Fetches each one once at
 * study load and writes the entries into the metadata, so the image loader sees a complete LUT. A
 * fetch that fails leaves the element as it was (the LUT is then dropped as incomplete, never black).
 */
export async function inlineLutBulkData(client: DicomWebClient, rows: DicomJson[]): Promise<void> {
  await Promise.all(rows.flatMap(bulkLutData).map(async ({ element, entries }) => {
    try {
      const fetched = await client.bulk(element.BulkDataURI as string, BULK_ACCEPT);
      if (fetched) element.Value = lutWords(firstPart(fetched), entries);
    } catch (error) {
      console.warn('[viewer] LUT bulk data unavailable', error);
    }
  }));
}

// --- Per-draw sync -----------------------------------------------------------------------------

interface LutImage extends CsImage {
  voiLUT?: CsLut;
  modalityLUT?: CsLut;
  voiLUTFunction?: VoiFunction;
  minPixelValue: number;
  maxPixelValue: number;
  slope: number;
  intercept: number;
}

interface ImageVoi {
  /** The window at which the image is shown exactly as its header says (the original LUT, if any). */
  anchor: VoiWindow;
  range: InputRange;
}

const imageVoi = new WeakMap<CsImage, ImageVoi>();
const lastKey = new WeakMap<HTMLElement, string>();
const lastLut = new WeakMap<HTMLElement, CsLut | undefined>();

function firstOf(value: number | number[] | undefined): number {
  return Array.isArray(value) ? Number(value[0]) : Number(value);
}

function inputRange(image: LutImage, modalityLUT: CsLut | undefined): InputRange {
  if (modalityLUT) {
    let lo = Infinity;
    let hi = -Infinity;
    for (const v of modalityLUT.lut) {
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    return { lo, hi };
  }
  const slope = Number.isFinite(image.slope) ? image.slope : 1;
  const intercept = Number.isFinite(image.intercept) ? image.intercept : 0;
  const a = image.minPixelValue * slope + intercept;
  const b = image.maxPixelValue * slope + intercept;
  return { lo: Math.min(a, b), hi: Math.max(a, b) };
}

function voiOf(enabled: CsEnabledElement, image: LutImage): ImageVoi {
  let known = imageVoi.get(image);
  if (!known) {
    const width = firstOf(image.windowWidth);
    const center = firstOf(image.windowCenter);
    const anchor = Number.isFinite(width) && width > 0 && Number.isFinite(center)
      ? { width, center }
      : (() => {
        const voi = window.cornerstone.getDefaultViewportForImage(enabled.element, image).voi;
        return { width: voi.windowWidth, center: voi.windowCenter };
      })();
    known = { anchor, range: inputRange(image, isUsableLut(image.modalityLUT) ? image.modalityLUT : undefined) };
    imageVoi.set(image, known);
  }
  return known;
}

/** The VOI LUT the viewport should render for the image on screen and its current window. */
function desiredVoiLut(image: LutImage, voi: ImageVoi, current: VoiWindow): CsLut | undefined {
  const original = isUsableLut(image.voiLUT) ? image.voiLUT : undefined;
  if (original) return sameWindow(current, voi.anchor) ? original : stretchLut(original, voi.anchor, current, voi.range) ?? original;
  const fn = image.voiLUTFunction ?? 'LINEAR';
  if (fn === 'LINEAR') return undefined;
  return functionLut(fn, current, voi.range);
}

/**
 * Runs before each draw (cornerstone PRE_RENDER, every animation frame): cheap unless the image or
 * window changed. cornerstone keeps the first image's LUTs on the viewport while a stack scrolls,
 * so this also replaces them with the LUTs of the image now on screen.
 */
export function syncViewportLut(enabled: CsEnabledElement): void {
  const image = enabled.image as LutImage | undefined;
  const viewport = enabled.viewport as (CsEnabledElement['viewport'] & { voiLUT?: CsLut; modalityLUT?: CsLut }) | undefined;
  if (!image || !viewport || image.color || (enabled as { layers?: unknown[] }).layers?.length) return;
  const { windowWidth, windowCenter } = viewport.voi;
  const key = `${image.imageId}|${windowWidth}|${windowCenter}`;
  if (lastKey.get(enabled.element) === key && viewport.voiLUT === lastLut.get(enabled.element)) return;
  const modalityLUT = isUsableLut(image.modalityLUT) ? image.modalityLUT : undefined;
  if (viewport.modalityLUT !== modalityLUT) {
    viewport.modalityLUT = modalityLUT;
    (enabled as { needsRedraw?: boolean }).needsRedraw = true;
  }
  const voiLUT = desiredVoiLut(image, voiOf(enabled, image), { width: windowWidth, center: windowCenter });
  if (viewport.voiLUT !== voiLUT) {
    viewport.voiLUT = voiLUT;
    (enabled as { needsRedraw?: boolean }).needsRedraw = true;
  }
  lastKey.set(enabled.element, key);
  lastLut.set(enabled.element, voiLUT);
}
